import React, { forwardRef, useContext, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import GetAndUploadTemplate from 'components/GetAndUploadTemplate';
import { FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import useCallbackAsync from 'hooks/useCallbackAsync';
import api from 'api';
import utils from 'utils';
import { useTranslation } from 'react-i18next';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import { EXCEL_FILE_TYPES } from 'data/global';

import './AddBulkMembersModal.styles.scss';

const FILE_NAME = 'enterpriseMembers';

type AddBulkMembersModalProps = {
  className?: string;
} & ModalProps;

const AddBulkMembersModal: React.ForwardRefRenderFunction<
  ModalRef,
  AddBulkMembersModalProps
> = (props, ref) => {
  const { className, ...rest } = props;

  const { enterpriseId, refetch } = useContext(EnterpriseMembersContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('add-bulk-members-modal', className);

  const { t } = useTranslation();

  const [downloadSpreadsheet, isDownloadingTemplate] = useCallbackAsync(
    async () => {
      const { data } = await api.enterprises.getBulkSpreadsheetTemplate(
        enterpriseId,
      );

      utils.downloadUrl({
        type: FILE_TYPES.DOCUMENT_XLSX,
        href: data,
      });
    },
  );

  const [uploadSpreadsheet] = useCallbackAsync(
    async (file, fileName, config) => {
      await api.enterprises.uploadBulkSpreadsheetTemplate(
        enterpriseId,
        file,
        fileName,
        config,
      );

      modal?.close();
      refetch();

      return null;
    },
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('addMembersInBulk')}
      {...rest}
    >
      <GetAndUploadTemplate
        fileName={FILE_NAME}
        supportedFileTypes={EXCEL_FILE_TYPES}
        isDownoadingTemplate={isDownloadingTemplate}
        downloadTemplate={downloadSpreadsheet}
        uploadRequest={uploadSpreadsheet}
      />

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(AddBulkMembersModal);
